import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";

const chatMarkdownSanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames || []),
    "h1", "h2", "h3", "h4", "h5", "h6",
    "p", "br", "hr",
    "strong", "em", "b", "i", "u",
    "ul", "ol", "li",
    "blockquote", "code", "pre",
    "a", "span",
  ],
  attributes: {
    ...defaultSchema.attributes,
    a: ["href", "title", "target", "rel"],
    "*": ["className"],
  },
  protocols: {
    href: ["http", "https", "mailto"],
  },
  strip: ["script", "style", "iframe", "object", "embed", "form", "input", "button"],
};

const isSafeMarkdownHref = (href: string | undefined): href is string => {
  if (!href) return false;
  if (href.startsWith("//")) return false;
  if (href.startsWith("/") || href.startsWith("#")) return true;
  try {
    const parsed = new URL(href);
    return ["http:", "https:", "mailto:"].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const chatMarkdownComponents = {
  ul: ({ children }: { children: React.ReactNode }) => (
    <ul className="list-disc pl-4 space-y-1 my-2">{children}</ul>
  ),
  ol: ({ children }: { children: React.ReactNode }) => (
    <ol className="list-decimal pl-4 space-y-1 my-2">{children}</ol>
  ),
  li: ({ children }: { children: React.ReactNode }) => (
    <li className="text-sm leading-relaxed">{children}</li>
  ),
  p: ({ children }: { children: React.ReactNode }) => (
    <p className="my-1.5 leading-relaxed">{children}</p>
  ),
  strong: ({ children }: { children: React.ReactNode }) => (
    <strong className="font-semibold text-foreground">{children}</strong>
  ),
  code: ({ children }: { children: React.ReactNode }) => (
    <code className="bg-muted px-1 py-0.5 rounded text-xs font-mono">{children}</code>
  ),
  h1: ({ children }: { children: React.ReactNode }) => (
    <h1 className="text-base font-semibold mt-3 mb-1">{children}</h1>
  ),
  h2: ({ children }: { children: React.ReactNode }) => (
    <h2 className="text-sm font-semibold mt-2 mb-1">{children}</h2>
  ),
  h3: ({ children }: { children: React.ReactNode }) => (
    <h3 className="text-sm font-medium mt-2 mb-1">{children}</h3>
  ),
  a: ({ href, children }: { href?: string; children: React.ReactNode }) => {
    if (!isSafeMarkdownHref(href)) {
      return <span>{children}</span>;
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
        {children}
      </a>
    );
  },
} as const;

interface ChatMarkdownBodyProps {
  content: string;
}

export function SafeChatMarkdown({ content, className = "" }: ChatMarkdownBodyProps & { className?: string }) {
  return (
    <div className={className}>
      <ReactMarkdown
        components={chatMarkdownComponents}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, chatMarkdownSanitizeSchema]]}
      >
        {content || ""}
      </ReactMarkdown>
    </div>
  );
}

export function ChatMarkdownBody({ content }: ChatMarkdownBodyProps) {
  return (
    <SafeChatMarkdown
      content={content}
      className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-headings:my-2 prose-strong:text-foreground"
    />
  );
}
